<template>
  <div id="summary" class="results-panel">
    <section v-if="activeTool === bioTool.name">
      <vs-divider position="left" class="divider">General</vs-divider>
      <div class="summary-container">
        <div class="position-summary">
          <vs-chip class="summary-chips custom-chip">
            <vs-avatar icon="local_offer" />
            {{ biologicalResults.specie.name }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="LON" />
            {{ biologicalResults.coords.lon }}
          </vs-chip>
          <vs-chip class="summary-chips custom-chip">
            <vs-avatar text="LAT" />
            {{ biologicalResults.coords.lat }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="INI" />
            {{ biologicalResults.dates.ini }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="END" />
            {{ biologicalResults.dates.end }}
          </vs-chip>
        </div>
      </div>
      <vs-divider position="left" class="divider"
        >Biological Thresholds</vs-divider
      >
      <div class="variable-summary">
        <vs-chip class="summary-chips">
          <vs-avatar text="MIN" />
          {{ biologicalResults.specie.temperature_min }}°
        </vs-chip>
        <vs-chip class="custom-chip summary-chips">
          <vs-avatar text="MAX" />
          {{ biologicalResults.specie.temperature_max }}°
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="MIN" />
          {{ biologicalResults.specie.salinity_min }}psu
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="MAX" />
          {{ biologicalResults.specie.salinity_max }}psu
        </vs-chip>
      </div>
      <vs-divider position="center" class="result-divider"
        >Biological Suitability Index</vs-divider
      >
      <div class="result-container">
        <vs-chip>
          <vs-avatar :icon="resultIcon" text class="index-avatar" />
          <span class="label-result">{{ result }}%</span>
        </vs-chip>
        <span :style="{ backgroundColor: resultColor }" class="dot" />
      </div>
      <label class="footer-label">
        * Data from {{ bioTool.config.sourceData }}
      </label>
    </section>
    <section v-if="activeTool === waveTool.name">
      <vs-divider position="left" class="divider">General</vs-divider>
      <div class="summary-container">
        <div class="position-summary">
          <vs-chip class="summary-chips">
            <vs-avatar text="LON" />
            {{ marineEnergyResults.coords.lon }}
          </vs-chip>
          <vs-chip class="summary-chips custom-chip">
            <vs-avatar text="LAT" />
            {{ marineEnergyResults.coords.lat }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="INI" />
            {{ marineEnergyResults.dates.start }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="END" />
            {{ marineEnergyResults.dates.end }}
          </vs-chip>
        </div>
      </div>
      <vs-divider position="left" class="divider"
        >Wave Energy Resource Thresholds</vs-divider
      >
      <div class="variable-summary">
        <vs-chip class="summary-chips">
          <vs-avatar text="MIN" />
          {{ marineEnergyResults.config.hs_min }}m
        </vs-chip>
        <vs-chip class="custom-chip summary-chips">
          <vs-avatar text="MAX" />
          {{ marineEnergyResults.config.hs_max }}m
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="MIN" />
          {{ marineEnergyResults.config.tp_min }}s
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="MAX" />
          {{ marineEnergyResults.config.tp_max }}s
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="CGE" />
          {{ marineEnergyResults.config.cge_min }}kw/m
        </vs-chip>
      </div>
      <vs-divider position="center" class="result-divider"
        >Wave Energy Suitability Index</vs-divider
      >
      <div class="result-container">
        <vs-chip>
          <vs-avatar :icon="resultIcon" text class="index-avatar" />
          <span class="label-result">{{ result }}%</span>
        </vs-chip>
        <span :style="{ backgroundColor: resultColor }" class="dot" />
      </div>
      <label class="footer-label">
        * Data from {{ waveTool.config.sourceData }}
      </label>
    </section>
    <section v-if="activeTool === windTool.name">
      <vs-divider position="left" class="divider">General</vs-divider>
      <div class="summary-container">
        <div class="position-summary">
          <vs-chip class="summary-chips">
            <vs-avatar text="LON" />
            {{ marineEnergyResults.coords.lon }}
          </vs-chip>
          <vs-chip class="summary-chips custom-chip">
            <vs-avatar text="LAT" />
            {{ marineEnergyResults.coords.lat }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="INI" />
            {{ marineEnergyResults.dates.start }}
          </vs-chip>
          <vs-chip class="summary-chips">
            <vs-avatar text="END" />
            {{ marineEnergyResults.dates.end }}
          </vs-chip>
        </div>
      </div>
      <vs-divider position="left" class="divider"
        >Wind Energy Resource Thresholds</vs-divider
      >
      <div class="variable-summary">
        <vs-chip class="custom-chip summary-chips">
          <vs-avatar text="MAX" />
          {{ marineEnergyResults.config.hs_max }}m
        </vs-chip>
        <vs-chip class="summary-chips">
          <vs-avatar text="POW" />
          {{ marineEnergyResults.config.pow }}W/m²
        </vs-chip>
      </div>
      <vs-divider position="center" class="result-divider"
        >Wind Energy Suitability Index</vs-divider
      >
      <div class="result-container">
        <vs-chip>
          <vs-avatar :icon="resultIcon" text class="index-avatar" />
          <span class="label-result">{{ result }}%</span>
        </vs-chip>
        <span :style="{ backgroundColor: resultColor }" class="dot" />
      </div>
      <label class="footer-label">
        * Data from {{ windTool.config.sourceData }}
      </label>
    </section>
  </div>
</template>
<script>
import { BTM_TOOLS } from "@/constants.js";
import { MSPTOOLS_API } from "@/config/apis";
import { mapState } from "vuex";
export default {
  data() {
    return {
      bioTool: {
        name: BTM_TOOLS.BIOLOGICAL_SUITABILITY,
        config: MSPTOOLS_API.biological
      },
      waveTool: {
        name: BTM_TOOLS.WAVE_ENERGY_SUITABILITY,
        config: MSPTOOLS_API.waveEnergy
      },
      windTool: {
        name: BTM_TOOLS.WIND_ENERGY_SUITABILITY,
        config: MSPTOOLS_API.windEnergy
      }
    };
  },
  computed: {
    ...mapState({
      activeTool: state => state.btmToolsActiveTool,
      biologicalResults: state => state.scToolsResults.biological,
      marineEnergyResults: state => state.scToolsResults.marineEnergy
    }),
    result() {
      return this.activeTool === this.bioTool.name
        ? parseInt(this.biologicalResults.index * 100)
        : parseInt(this.marineEnergyResults.index * 100);
    },
    resultIcon() {
      if (this.result < 40) {
        return "thumb_down";
      } else if (this.result < 70) {
        return "thumbs_up_down";
      }
      return "thumb_up";
    },
    resultFontColor() {
      if (this.result < 20 || this.result >= 90) {
        return "#ffffff";
      }
      return "#000000";
    },
    resultColor() {
      if (this.result < 10) {
        return "#a50026";
      } else if (this.result < 20) {
        return "#d73027";
      } else if (this.result < 30) {
        return "#f46d43";
      } else if (this.result < 40) {
        return "#fdae61";
      } else if (this.result < 50) {
        return "#fee08b";
      } else if (this.result < 60) {
        return "#d9ef8b";
      } else if (this.result < 70) {
        return "#a6d96a";
      } else if (this.result < 80) {
        return "#66bd63";
      } else if (this.result < 90) {
        return "#1a9850";
      }
      return "#006837";
    }
  }
};
</script>
<style scoped>
.divider {
  margin-bottom: 5px;
  margin-top: 10px;
}
.results-panel {
  flex-grow: 1;
  width: 100%;
}
.result-container {
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  justify-content: center;
  margin: 1rem;
}
.result-badge {
  font-size: 1.1rem;
  min-width: 300px;
}
.position-summary {
  display: flex;
  flex-grow: 1;
  justify-content: center;
  margin: 1rem;
}
.variable-summary {
  display: flex;
  flex-grow: 1;
  justify-content: center;
  margin: 1rem;
}
.custom-chip {
  margin-right: 2.5rem;
}
.con-vs-chip .con-vs-avatar {
  background: #19535f !important;
}
.summary-chips {
  background: none;
}
.result-divider {
  margin-top: 30px;
}

.label-result {
  font-size: 1.4rem;
  margin-right: 5px;
}
</style>
